import { useGetMany, type Identifier, type SortPayload } from "ra-core";
import { useMemo } from "react";

import type { Contact, Task } from "../../types";
import { getFullName } from "../cardModel";
import { useInfiniteRecords } from "../useInfiniteRecords";

/** Server-paginated tasks; the next 25 load with `fetchNextPage`. */
export const useInfiniteTasks = (
  filter: Record<string, unknown>,
  sort: SortPayload,
) => {
  const { records: tasks, ...pagination } = useInfiniteRecords<Task>(
    "tasks",
    filter,
    sort,
  );
  return { tasks, ...pagination };
};

/** Names of the cards (contacts) the given tasks are linked to. */
export const useCardNames = (tasks: Task[]) => {
  const ids = useMemo(
    () =>
      [
        ...new Set(
          tasks.flatMap((task) =>
            task.contact_id != null ? [task.contact_id] : [],
          ),
        ),
      ] as Identifier[],
    [tasks],
  );
  const { data: contacts = [] } = useGetMany<Contact>(
    "contacts",
    { ids },
    { enabled: ids.length > 0 },
  );
  return useMemo(
    () =>
      new Map<Identifier, string>(
        contacts.map((contact) => [contact.id, getFullName(contact)]),
      ),
    [contacts],
  );
};
